import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MarinaService } from 'src/app/services/marina.service';
import { UserService } from 'src/app/services/user.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import { Marina } from 'src/app/models';
import { NgxSpinnerService } from 'ngx-spinner';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageUtil } from 'src/app/utils/message.util';
import { RoleService } from 'src/app/services/role.service';
import { LoginMarinaOptionsComponent } from '../login-marina-options/login-marina-options.component';
import Swal from 'sweetalert2';

const PASSWORD_MIN_LENGTH = 8;

function passwordStrengthValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    const v = control.value;
    if (!v || v.length < PASSWORD_MIN_LENGTH) return { minLength: true };
    if (!/[A-Z]/.test(v)) return { noUpperCase: true };
    if (!/[0-9]/.test(v)) return { noNumber: true };
    if (!/[^A-Za-z0-9]/.test(v)) return { noSpecial: true };
    return null;
  };
}

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit {
  passwordRules = [
    'Mínimo 8 caracteres',
    'Ao menos uma letra maiúscula',
    'Ao menos um número',
    'Ao menos um caractere especial',
    'Não pode ser igual à última senha utilizada'
  ];
  form = new UntypedFormGroup({
    newPassword: new UntypedFormControl('', Validators.compose([
      Validators.required,
      passwordStrengthValidator()
    ])),
    confirmPassword: new UntypedFormControl('', Validators.required)
  }, { validators: this.passwordMatchValidator });
  returnUrl = '/app';
  messageError: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private marinaService: MarinaService,
    private spinner: NgxSpinnerService,
    private dialog: DialogService,
    private messageUtil: MessageUtil,
    private roleService: RoleService
  ) {}

  passwordMatchValidator(g: UntypedFormGroup): { [key: string]: boolean } | null {
    const newP = g.get('newPassword')?.value;
    const confirm = g.get('confirmPassword')?.value;
    if (newP && confirm && newP !== confirm) {
      return { mismatch: true };
    }
    return null;
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams.returnUrl || '/app';
    if (!StorageUtil.getToken()) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: this.returnUrl } });
    }
  }

  submit(): void {
    this.messageError = null;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const userId = StorageUtil.getUser()?.userId;
    if (!userId) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: this.returnUrl } });
      return;
    }
    this.spinner.show();
    this.userService.changePasswordExpired(userId, this.form.get('newPassword').value).subscribe({
      next: () => {
        this.completeLogin();
      },
      error: (err) => {
        this.spinner.hide();
        this.messageError = err?.error?.message || 'Erro ao alterar senha.';
      }
    });
  }

  private completeLogin(): void {
    const token = StorageUtil.getToken();
    const refreshToken = StorageUtil.getRefreshToken();
    const tokenObj = { token, refreshToken };
    const marinas = StorageUtil.getMarinasIds();
    if (!marinas || marinas.length === 0) {
      this.spinner.hide();
      this.router.navigate([this.returnUrl]);
      return;
    }
    if (marinas.length > 1) {
      const userId = StorageUtil.getUser().userId;
      this.dialog.open(LoginMarinaOptionsComponent, {
        width: '50vh',
        height: '80vh',
        data: userId,
        header: this.messageUtil.translateKey('SELECT-MARINA'),
      }).onClose.subscribe(marinaId => {
        if (marinaId != null) {
          this.applyMarinaAndNavigate(marinaId, tokenObj);
        } else {
          this.spinner.hide();
        }
      });
    } else {
      this.applyMarinaAndNavigate(marinas[0], tokenObj);
    }
  }

  private applyMarinaAndNavigate(marinaId: number, tokenObj: { token: string; refreshToken: string }): void {
    this.marinaService.getMarina(marinaId).subscribe({
      next: (marina: Marina) => {
        if (marina.disableWebOperation) {
          this.roleService.isCollaboratorOrAdmin(StorageUtil.getUser().userId, marina.id).subscribe({
            next: (resp) => {
              if (resp) {
                StorageUtil.login(tokenObj.token, tokenObj.refreshToken, marina);
                this.finishLogin();
              } else {
                Swal.fire(
                  this.messageUtil.translateKey('SUMMARY.WARNING'),
                  'Entre em contato com o administrador do sistema.',
                  'warning'
                );
                this.spinner.hide();
              }
            },
            error: () => this.spinner.hide()
          });
        } else {
          StorageUtil.login(tokenObj.token, tokenObj.refreshToken, marina);
          this.finishLogin();
        }
      },
      error: () => this.spinner.hide()
    });
  }

  private finishLogin(): void {
    this.userService.getById(StorageUtil.getUser().userId).subscribe({
      next: (user) => {
        user.password = '';
        user.refreshToken = '';
        localStorage.setItem('usuarioPesquisa', JSON.stringify(user));
        this.router.navigate([this.returnUrl]);
        this.spinner.hide();
      },
      error: () => {
        this.router.navigate([this.returnUrl]);
        this.spinner.hide();
      }
    });
  }

  isInvalid(control: AbstractControl): boolean {
    return control && control.touched && control.dirty && !control.valid;
  }
}
